import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, ChevronNextIcon, ChevronPrevIcon, JobsIcon, ListIcon, PlusIcon } from '@/components/icons';
import { JobRow } from '@/components/jobs/JobRow';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useActiveJobs, type JobView } from '@/hooks/useActiveJobs';
import { formatJobDate, formatMonthTitle, monthGrid, todayInIsrael, WEEKDAY_SHORT_HE } from '@/lib/format';

type View = 'list' | 'calendar';

export function JobsPage() {
  const jobs = useActiveJobs();
  const [view, setView] = useState<View>('list');

  return (
    <>
      <PageHeader
        title="עבודות"
        subtitle={jobs.state === 'ready' ? `${jobs.data.length} עבודות פעילות` : undefined}
        actions={
          <Link
            to="/jobs/new"
            className="inline-flex min-h-11 items-center gap-2 rounded-control bg-brand px-4 text-[14px] font-semibold text-white hover:bg-brand-strong"
          >
            <PlusIcon size={18} />
            <span>עבודה חדשה</span>
          </Link>
        }
      />

      <div role="tablist" aria-label="תצוגה" className="mb-5 inline-flex rounded-control bg-sunken p-1">
        <ViewTab active={view === 'list'} onClick={() => setView('list')} icon={<ListIcon size={18} />} label="רשימה" />
        <ViewTab
          active={view === 'calendar'}
          onClick={() => setView('calendar')}
          icon={<CalendarIcon size={18} />}
          label="לוח שנה"
        />
      </div>

      {jobs.state === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}
      {jobs.state === 'error' && <Notice tone="error">{jobs.message}</Notice>}
      {jobs.state === 'ready' &&
        (jobs.data.length === 0 ? (
          <EmptyState
            icon={<JobsIcon size={30} />}
            title="אין עבודות פעילות"
            body="אחרי יצירת עבודה יופיעו כאן התאריך, המיקום וכמות העובדים שדווחה."
          />
        ) : view === 'list' ? (
          <ul className="space-y-3">
            {jobs.data.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </ul>
        ) : (
          <CalendarView jobs={jobs.data} />
        ))}
    </>
  );
}

function ViewTab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'inline-flex min-h-10 items-center gap-2 rounded-[8px] px-4 text-[14px] font-medium transition-colors',
        active ? 'bg-surface text-ink shadow-sm' : 'text-ink-2 hover:text-ink',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}

function CalendarView({ jobs }: { jobs: JobView[] }) {
  const today = todayInIsrael();
  const [cursor, setCursor] = useState(() => ({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 }));
  const [selected, setSelected] = useState<string>(today);

  const byDate = useMemo(() => {
    const map = new Map<string, JobView[]>();
    for (const job of jobs) map.set(job.date, [...(map.get(job.date) ?? []), job]);
    return map;
  }, [jobs]);

  const cells = monthGrid(cursor.y, cursor.m);
  const move = (delta: number) =>
    setCursor(({ y, m }) => {
      const d = new Date(Date.UTC(y, m + delta, 1));
      return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
    });
  const selectedJobs = byDate.get(selected) ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <section className="rounded-card border border-line bg-surface p-4" aria-label="לוח שנה">
        <div className="mb-3 flex items-center justify-between">
          <button type="button" onClick={() => move(-1)} aria-label="החודש הקודם" className="flex size-10 items-center justify-center rounded-full hover:bg-sunken">
            <ChevronPrevIcon />
          </button>
          <h2 className="text-[16px] font-semibold">{formatMonthTitle(cursor.y, cursor.m)}</h2>
          <button type="button" onClick={() => move(1)} aria-label="החודש הבא" className="flex size-10 items-center justify-center rounded-full hover:bg-sunken">
            <ChevronNextIcon />
          </button>
        </div>
        <div className="grid grid-cols-7 text-center text-[12px] font-medium text-ink-3">
          {WEEKDAY_SHORT_HE.map((d) => (
            <div key={d} className="py-1.5">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} />;
            const dayJobs = byDate.get(date) ?? [];
            const workers = dayJobs.reduce((s, j) => s + (j.totalWorkers || 0), 0);
            const isSelected = date === selected;
            const isToday = date === today;
            return (
              <button
                key={date}
                type="button"
                onClick={() => setSelected(date)}
                aria-pressed={isSelected}
                aria-label={`${formatJobDate(date)}${dayJobs.length ? `, ${dayJobs.length} עבודות, ${workers} עובדים` : ''}`}
                className={[
                  'flex aspect-square flex-col items-center justify-start rounded-[8px] pt-1.5 text-[14px] transition-colors',
                  isSelected ? 'bg-brand text-white' : 'hover:bg-sunken',
                  isToday && !isSelected ? 'ring-2 ring-brand ring-inset' : '',
                ].join(' ')}
              >
                <span className="tabular">{Number(date.slice(8))}</span>
                {dayJobs.length > 0 && (
                  <span
                    className={[
                      'tabular mt-auto mb-1.5 rounded px-1 text-[11px] font-bold',
                      isSelected ? 'bg-accent text-white' : 'bg-tag text-tag-ink',
                    ].join(' ')}
                  >
                    {workers}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section aria-live="polite">
        <h2 className="mb-3 text-[16px] font-semibold">{formatJobDate(selected)}</h2>
        {selectedJobs.length === 0 ? (
          <p className="rounded-card border border-dashed border-line-strong p-6 text-center text-[14px] text-ink-2">
            אין עבודות ביום הזה
          </p>
        ) : (
          <ul className="space-y-3">
            {selectedJobs.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
